import './GraphLayoutItem.css';

import type { GraphPathInfo, Layout } from '@ipfabric/utils';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { GraphLayoutIcon } from 'components/Graph/GraphLayout/icons/GraphLayoutIcon';
import { SaveIcon } from 'components/Graph/GraphSidePanel/icons/SaveIcon';
import { isFallbackLayout } from 'components/Graph/helpers/isFallbackLayout';
import { useLocale } from 'containers/App/LocaleContext';
import { type FC, memo } from 'react';
import { FormattedMessage } from 'react-intl';

export interface GraphLayoutChangeEvent {
  layout: Layout;
}

interface Props {
  checked: boolean;
  description: string;
  layout: Layout;
  layoutItemClassName?: string;
  partialLayout: boolean;
  pathInfo?: GraphPathInfo;
  title: string;
  onChange: (event: GraphLayoutChangeEvent) => void;
  onSave?: () => void;
  unsavedChanges?: boolean;
}

const BareGraphLayoutItem: FC<Props> = (props) => {
  const msgGraphLayout = useLocale().canvasGraph.graphLayout;
  const inputId = `graph-layout-${props.layout}`;
  const handleLayoutChange = () => {
    props.onChange({ layout: props.layout });
  };

  const handleButtonClick = () => {
    // Button is suddenly not fired on label click.
    // NIM-5558 needed calling 'preventDefault' but mysteriously, something fixed this
    // and 'preventDefault' could be removed in NIM-6108.
    // @TODO: Investigate why this works.
    props.onSave!();
  };

  const isFallbacked =
    !props.unsavedChanges &&
    !props.partialLayout &&
    props.pathInfo &&
    isFallbackLayout(props.pathInfo) &&
    props.pathInfo.defaultLayout === props.layout;

  return (
    <label
      className={classNames(
        'mb-0 flex cursor-pointer font-normal',
        props.layoutItemClassName,
      )}
      htmlFor={inputId}
      // Handle layout change on label instead of input to prevent calling button click event.
      // When label was clicked, input onChange and button onClick events were called.
      onClick={handleLayoutChange}
    >
      <GraphLayoutIcon
        title={null}
        className='mt-2.5'
        layout={props.layout}
        width={16}
        height={16}
      />
      <span className='py-lgu-3 mt-lgu-1 ml-2.5 mr-5 flex-1'>
        <span className='graph-layout-item__title'>{props.title}</span>
        <span className='graph-layout-item__description'>
          {props.description}
        </span>
        {isFallbacked && (
          <span className='graph-layout-item__fallback-warning'>
            <FormattedMessage
              id='graphLayoutItem.fallback'
              defaultMessage={msgGraphLayout.fallback}
              values={{
                fallbackAlgorithm:
                  msgGraphLayout[props.pathInfo!.layoutAlgorithm].title,
              }}
            />
          </span>
        )}
      </span>
      <span className='flex items-center'>
        {props.onSave && (
          <Button
            variant='primary'
            className='button--square mr-3'
            type='button'
            onClick={handleButtonClick}
          >
            <SaveIcon title='Save layout' width='16' height='16' />
          </Button>
        )}
        <input
          checked={props.checked}
          className='ipf-radio'
          id={inputId}
          // onChange replaced by onClick on label element
          readOnly
          type='radio'
        />
      </span>
    </label>
  );
};

export const GraphLayoutItem = memo(BareGraphLayoutItem);
